import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { ListRow } from "components/designV2/listRow/listRow";
import { MetricCell } from "components/designV2/metricCell/metricCell";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import SimflexityPayLogo from "images/logo/svg/simflexityPay-logo.svg";
import { PaymentMethod } from "model/Gig";
import { CompanyWorkerSalary } from "model/Salary";
import { useTranslation } from "react-i18next";
import {
  ActionButtons,
  IconButton,
} from "web-apps/company/components/timeReportsV2/components/views/person/components/row/personSummaryRow.styled";
import { Logo, LogoContainer } from "./salary.styled";
import {
  RowCard,
  RowGrid,
  RowTop,
  AvatarAndActions,
  RowMetrics,
} from "./salaryListRow.styles";

export interface SalaryListRowProps {
  salary: CompanyWorkerSalary;
  avatarUrl?: string;
  onOpenProfile: (workerId: number) => void;
  onOpenEdit: (salary: CompanyWorkerSalary) => void;
}

export const SalaryListRow = ({
  salary,
  avatarUrl,
  onOpenProfile,
  onOpenEdit,
}: SalaryListRowProps): JSX.Element => {
  const { t } = useTranslation();

  const isTrainee = salary.employment.employmentType === "Trainee";
  const isSimflexityEmployee =
    salary.employment.employmentType === "SimflexityEmployee";
  const isSimflexityPay =
    salary.employment.paymentType === PaymentMethod.SIMFLEXITY_PAY;
  const currency = "kr";

  const title = `${salary.firstName} ${salary.lastName}${
    !salary.isActive ? ` ${t("Salary.Inactive")}` : ""
  }`;

  const salaryLabel = isTrainee
    ? t("Salary.Salary")
    : t(`Salary.${salary.employment.salaryType}`);
  const salaryValue = isTrainee
    ? "-"
    : `${salary.employment.salary} ${currency}`;

  const workPercentageValue = isTrainee
    ? "-"
    : salary.employment.salaryType === "Monthly"
      ? `${salary.employment.workPercentage * 100}%`
      : t("Salary.Hours");

  const taxValue =
    isTrainee || isSimflexityEmployee ? "-" : salary.employment.taxValue;

  const startDate = salary.employment.startDate.split("T")[0];
  const applyFromValue = salary.isActive
    ? startDate
    : `${startDate} - ${salary.employment.endDate.split("T")[0]}`;

  return (
    <ListRow>
      <RowCard $isInactive={!salary.isActive}>
        <RowGrid>
          <RowTop>
            <AvatarChip
              title={title}
              subTitle={t("General.Giggers")}
              avatarUrl={avatarUrl}
              onAvatarClick={() => onOpenProfile(salary.workerId)}
              titleIcon={
                isSimflexityPay ? (
                  <LogoContainer>
                    <Logo src={SimflexityPayLogo} alt="SimflexityPay logo" />
                  </LogoContainer>
                ) : null
              }
            />
            <AvatarAndActions>
              <ActionButtons>
                <TooltipHover text={t("Salary.UpdateSalary")}>
                  <IconButton onClick={() => onOpenEdit(salary)}>
                    <CustomIcon
                      name="v2/pen"
                      size="20px"
                      color={ColorV2.Blue1}
                    />
                  </IconButton>
                </TooltipHover>
              </ActionButtons>
            </AvatarAndActions>
          </RowTop>

          <RowMetrics>
            <MetricCell value={salaryValue} label={salaryLabel} />
            <MetricCell
              value={workPercentageValue}
              label={t("Salary.WorkPercentage")}
            />
            <MetricCell
              value={taxValue.toString()}
              label={t("OrganizationalStructure.TaxTable")}
            />
            <MetricCell
              value={t(
                `OrganizationalStructure.${salary.employment.employmentType}`,
              )}
              label={t("OrganizationalStructure.EmploymentType")}
            />
            <MetricCell value={applyFromValue} label={t("Salary.ApplyFrom")} />
          </RowMetrics>
        </RowGrid>
      </RowCard>
    </ListRow>
  );
};
